import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { BadgeCheck, XCircle, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { SITE } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentResult() {
  const [searchParams] = useSearchParams();
  const result = searchParams.get("result") || "problem";
  const order = searchParams.get("order") || "";
  const [info, setInfo] = useState(null);
  const [checking, setChecking] = useState(true);
  const attempts = useRef(0);

  useEffect(() => {
    if (!order) { setChecking(false); return; }
    let cancelled = false;
    const check = async () => {
      try {
        const res = await axios.get(`${API}/payments/status`, { params: { order } });
        if (cancelled) return;
        setInfo(res.data);
        const stillPending = res.data.payment_status !== "paid_online" && res.data.payment_status !== "needs_review";
        if (result === "success" && stillPending && attempts.current < 24) {
          attempts.current += 1;
          setTimeout(check, 2500);
          return;
        }
      } catch { /* order not found */ }
      if (!cancelled) setChecking(false);
    };
    check();
    return () => { cancelled = true; };
  }, [order, result]);

  const paid = info?.payment_status === "paid_online";
  const isCopy = info?.type === "copy_request";
  const success = result === "success";
  const declined = result === "declined";

  const headerColor = success ? "bg-[#0E9AAA]" : declined ? "bg-red-500" : "bg-amber-500";
  const Icon = success ? BadgeCheck : declined ? XCircle : AlertTriangle;
  const title = success
    ? "Payment Successful!"
    : declined
    ? "Payment Declined"
    : "Payment Could Not Be Completed";

  return (
    <div className="bg-[#F6FAFB] min-h-screen py-16" data-testid="payment-result-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <Card className="rounded-2xl border-slate-100 shadow-lg overflow-hidden">
          <div className={`${headerColor} p-8 text-center`}>
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
              {checking && success && !paid
                ? <Loader2 className="text-white animate-spin" size={32} />
                : <Icon className="text-white" size={32} />}
            </div>
            <h1 className="text-2xl font-extrabold text-white" data-testid="payment-result-title">{title}</h1>
            {info?.confirmation_number || info?.request_number ? (
              <p className="text-white/90 mt-2 text-sm">
                {isCopy ? "Request" : "Booking"}: <span className="font-extrabold">{info.confirmation_number || info.request_number}</span>
              </p>
            ) : null}
          </div>
          <CardContent className="p-8">
            {success && paid && (
              <>
                <div className="grid sm:grid-cols-2 gap-5 text-sm">
                  {isCopy ? (
                    <>
                      <div><div className="text-slate-400 font-semibold uppercase text-xs">Service</div><div className="font-bold text-[#0A2540] mt-1">Certified Copy of Test Results</div></div>
                      <div><div className="text-slate-400 font-semibold uppercase text-xs">Copies Requested</div><div className="font-bold text-[#0A2540] mt-1">{info.quantity}</div></div>
                    </>
                  ) : (
                    <>
                      <div><div className="text-slate-400 font-semibold uppercase text-xs">Test</div><div className="font-bold text-[#0A2540] mt-1">{info.test_name}</div></div>
                      <div><div className="text-slate-400 font-semibold uppercase text-xs">Date & Time</div><div className="font-bold text-[#0A2540] mt-1">{info.date_display} at {info.time_slot}</div></div>
                    </>
                  )}
                  <div><div className="text-slate-400 font-semibold uppercase text-xs">Amount Paid</div><div className="font-bold text-[#0A2540] mt-1" data-testid="amount-paid">CI ${Number(info.amount_paid || info.online_total).toFixed(2)}</div></div>
                  <div><div className="text-slate-400 font-semibold uppercase text-xs">Payment Status</div><div className="font-bold text-emerald-600 mt-1">Paid Online</div></div>
                </div>
                <p className="text-sm text-slate-600 mt-6 bg-[#F6FAFB] border border-slate-100 rounded-xl p-4">
                  {isCopy
                    ? "Your request is being processed. We'll contact you as soon as your certified copy is ready for pickup at 93 Hospital Rd, SIGMA Building, 1st Flr, George Town (Mon–Fri 9:00 AM – 3:00 PM). Please bring a valid photo ID when collecting. A receipt has been emailed to you."
                    : "Your payment is complete — no payment is due at check in. Please still arrive 20 minutes early with 2 forms of ID (one being your passport)."}
                </p>
              </>
            )}
            {success && !paid && !checking && (
              <p className="text-sm text-slate-600" data-testid="payment-complete-generic">
                {info?.payment_status === "needs_review"
                  ? "Your payment was approved and is being verified by our team — your status will update shortly, no action is needed from you. Questions? Call us at " + SITE.phone + "."
                  : "Your payment has been completed — thank you! A receipt is on its way to your email." + (isCopy
                    ? " We'll contact you as soon as your certified copy is ready for pickup."
                    : " No payment is due at check in — please still arrive 20 minutes early with 2 forms of ID (one being your passport).")}
              </p>
            )}
            {success && !paid && checking && (
              <p className="text-sm text-slate-600 flex items-center gap-2">
                <Loader2 size={15} className="animate-spin" /> Confirming your payment — it's safe to leave this page, your status will update automatically.
              </p>
            )}
            {declined && (
              <p className="text-sm text-slate-600">
                {isCopy
                  ? "Your card was declined by the bank and no payment was taken, so your certified copy request was not submitted. Please try again with a different card, or call us at " + SITE.phone + " to arrange your copy."
                  : "Your card was declined by the bank and no payment was taken. You can try again with a different card from the Find My Booking page, or simply pay by cash, card or cheque at check in. Your booking is still confirmed."}
              </p>
            )}
            {!success && !declined && (
              <p className="text-sm text-slate-600">
                {isCopy
                  ? `Something went wrong while processing your payment and your certified copy request was not submitted. Please try again or call us at ${SITE.phone}.`
                  : `Something went wrong while processing your payment and it was not completed. Your booking is still confirmed — you can try again from the Find My Booking page or pay at check in. Need help? Call us at ${SITE.phone}.`}
              </p>
            )}
            <div className="mt-7 flex flex-wrap gap-4">
              {isCopy ? (
                !paid && (
                  <Link to="/certified-copy">
                    <Button data-testid="result-retry-copy-btn" className="bg-[#0A2540] hover:bg-[#123B63] text-white font-bold rounded-lg">Try Again</Button>
                  </Link>
                )
              ) : (
                <Link to="/my-booking">
                  <Button data-testid="result-mybooking-btn" className="bg-[#0A2540] hover:bg-[#123B63] text-white font-bold rounded-lg">Find My Booking</Button>
                </Link>
              )}
              <Link to="/">
                <Button variant="ghost" className="font-bold text-slate-500">Back to Home</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
